import { Fragment, useState } from 'react';
import { IconButton, Menu, MenuItem, Divider, Tooltip } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IWorkerSummary } from 'types/INodeAgent';

export interface WorkerActionsMenuProps {
  worker: IWorkerSummary;
  pendingFlags: {
    isRetiring: boolean;
    isStopping: boolean;
    isUpgrading: boolean;
    isCleaningUp: boolean;
    isRemoving: boolean;
    isDeleting: boolean;
    isRestarting: boolean;
  };
  onRetire?: () => void;
  onStop?: () => void;
  onUpgrade?: () => void;
  onCleanup?: () => void;
  onRemoveManaged?: () => void;
  onDeleteStandalone?: () => void;
  onRestart?: () => void;
}

interface ActionItem {
  key: string;
  label: string;
  onClick: () => void;
  destructive?: boolean;
  disabled: boolean;
  // Tooltip shown on hover when the item is disabled because the worker isn't
  // in the right state. Omitted for items disabled mid-action (the label
  // already says "Retiring…" etc, no need to explain twice).
  disabledReason?: string;
}

function buildItems(props: WorkerActionsMenuProps): ActionItem[] {
  const {
    worker,
    pendingFlags,
    onRetire,
    onStop,
    onUpgrade,
    onCleanup,
    onRemoveManaged,
    onDeleteStandalone,
    onRestart,
  } = props;
  const items: ActionItem[] = [];
  const isManaged = worker.managed !== false;
  const isFailedOrMissing =
    worker.status === 'CLEANUP_FAILED' ||
    worker.status === 'START_FAILED' ||
    worker.status === 'UPGRADE_FAILED' ||
    worker.status === 'MISSING';

  if (isManaged) {
    // Always show all canonical lifecycle actions; disable + tooltip
    // inapplicable ones so the menu shape is the same on every row.
    const retireStateBlocked = worker.status !== 'RUNNING' || !worker.worker_id || !worker.instance_id;
    const retireBlocked = retireStateBlocked || pendingFlags.isUpgrading;
    items.push({
      key: 'retire',
      label: pendingFlags.isRetiring ? 'Retiring…' : 'Retire',
      onClick: onRetire ?? (() => {}),
      disabled: retireBlocked || pendingFlags.isRetiring || !onRetire,
      disabledReason: pendingFlags.isRetiring
        ? undefined
        : pendingFlags.isUpgrading && !retireStateBlocked
          ? 'Cannot retire during an upgrade'
          : retireStateBlocked
            ? 'Only available while running'
            : undefined,
    });

    const upgradeStateBlocked = worker.status !== 'RUNNING' || !worker.instance_id;
    const upgradeBlocked = upgradeStateBlocked || pendingFlags.isRetiring;
    items.push({
      key: 'upgrade',
      label: pendingFlags.isUpgrading ? 'Upgrading…' : 'Upgrade',
      onClick: onUpgrade ?? (() => {}),
      disabled: upgradeBlocked || pendingFlags.isUpgrading || !onUpgrade,
      disabledReason: pendingFlags.isUpgrading
        ? undefined
        : pendingFlags.isRetiring && !upgradeStateBlocked
          ? 'Cannot upgrade during retirement'
          : upgradeStateBlocked
            ? 'Only available while running'
            : undefined,
    });

    // Restart sits with the non-destructive actions because it revives a worker
    // (vs Clean Up below, which destroys the workdir). Only meaningful from
    // STOPPED — the agent still tracks the ManagedWorker and its venv, so we
    // can spin a fresh worker process without reprovisioning.
    const restartBlocked = worker.status !== 'STOPPED' || !worker.instance_id;
    items.push({
      key: 'restart',
      label: pendingFlags.isRestarting ? 'Restarting…' : 'Restart',
      onClick: onRestart ?? (() => {}),
      disabled: restartBlocked || pendingFlags.isRestarting || !onRestart,
      disabledReason: restartBlocked && !pendingFlags.isRestarting ? 'Only available when stopped' : undefined,
    });

    const stopBlocked = worker.status !== 'RETIRING' || !worker.instance_id;
    items.push({
      key: 'stop',
      label: pendingFlags.isStopping ? 'Stopping…' : 'Stop',
      onClick: onStop ?? (() => {}),
      destructive: true,
      disabled: stopBlocked || pendingFlags.isStopping || !onStop,
      disabledReason: stopBlocked && !pendingFlags.isStopping ? 'Only available while retiring' : undefined,
    });

    const cleanupBlocked = worker.status !== 'STOPPED' || !worker.instance_id;
    items.push({
      key: 'cleanup',
      label: pendingFlags.isCleaningUp ? 'Cleaning up…' : 'Clean Up',
      onClick: onCleanup ?? (() => {}),
      destructive: true,
      disabled: cleanupBlocked || pendingFlags.isCleaningUp || !onCleanup,
      disabledReason: cleanupBlocked && !pendingFlags.isCleaningUp ? 'Only available when stopped' : undefined,
    });

    const removeBlocked = !isFailedOrMissing || !worker.instance_id;
    items.push({
      key: 'remove-managed',
      label: pendingFlags.isRemoving ? 'Removing…' : 'Remove',
      onClick: onRemoveManaged ?? (() => {}),
      destructive: true,
      disabled: removeBlocked || pendingFlags.isRemoving || !onRemoveManaged,
      disabledReason: removeBlocked && !pendingFlags.isRemoving ? 'Only available when failed or missing' : undefined,
    });
  } else {
    const retireBlocked = worker.status !== 'RUNNING' || !worker.worker_id;
    items.push({
      key: 'retire-standalone',
      label: pendingFlags.isRetiring ? 'Retiring…' : 'Retire',
      onClick: onRetire ?? (() => {}),
      disabled: retireBlocked || pendingFlags.isRetiring || !onRetire,
      disabledReason: retireBlocked && !pendingFlags.isRetiring ? 'Only available while running' : undefined,
    });

    const removeBlocked = worker.status !== 'MISSING' || !worker.worker_id;
    items.push({
      key: 'delete-standalone',
      label: pendingFlags.isDeleting ? 'Removing…' : 'Remove',
      onClick: onDeleteStandalone ?? (() => {}),
      destructive: true,
      disabled: removeBlocked || pendingFlags.isDeleting || !onDeleteStandalone,
      disabledReason: removeBlocked && !pendingFlags.isDeleting ? 'Only available when missing' : undefined,
    });
  }

  return items;
}

export default function WorkerActionsMenu(props: WorkerActionsMenuProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const items = buildItems(props);

  if (items.length === 0) return null;

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const firstDestructiveIdx = items.findIndex((i) => i.destructive);

  return (
    <>
      <IconButton size="small" aria-label="Worker actions" onClick={handleOpen}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {items.map((item, idx) => {
          const showDivider = firstDestructiveIdx > 0 && idx === firstDestructiveIdx;
          const menuItem = (
            <MenuItem
              disabled={item.disabled}
              onClick={() => {
                handleClose();
                item.onClick();
              }}
              sx={item.destructive ? { color: 'error.main' } : undefined}
            >
              {item.label}
            </MenuItem>
          );
          // MUI Tooltip can't detect pointer events on a disabled element,
          // so wrap disabled items with a reason in a span the Tooltip can
          // hover-detect. Enabled items render bare so Menu keyboard nav and
          // focus management aren't disrupted by the wrapper.
          // `display: block` on the span — otherwise consecutive disabled
          // items flow inline and end up on the same line.
          const rendered =
            item.disabled && item.disabledReason ? (
              <Tooltip title={item.disabledReason} placement="left">
                <span style={{ display: 'block' }}>{menuItem}</span>
              </Tooltip>
            ) : (
              menuItem
            );
          return (
            <Fragment key={item.key}>
              {showDivider && <Divider />}
              {rendered}
            </Fragment>
          );
        })}
      </Menu>
    </>
  );
}
